import {ReactElement, useState} from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
  DialogContent,
  Grid,
  InputAdornment,
  TextField,
  Button,
  ToggleButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import {DateCalendar} from "@mui/x-date-pickers";
import {Formik} from "formik";
import dayjs from "dayjs";
import {DemoContainer} from "@mui/x-date-pickers/internals/demo";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LoanService} from "services/LoanService";
import Utils from "shared/helpers/Utils";
import {
  AppFormikTextField,
  AppPrimaryButton,
} from "shared/components/AppComponents";
import {rndLoanCalculatorSchema} from "shared/helpers/Schema";

interface LoanCalculatorProps {
  showForm: boolean;
  onCloseForm: () => void;
}

interface LoanInfoProps {
  estimatedRefund: string;
  maximumLoanAmount: string;
  expectedRepaymentAmount: string;
  totalFeesAndCharges: string;
}

interface LoanDetailsProps {
  loanTerm: number;
  ezifinLoanInfo: LoanInfoProps;
  competitorLoanInfo: LoanInfoProps;
}

const LoanCalculator = (props: LoanCalculatorProps): ReactElement => {
  const {showForm, onCloseForm} = props;
  const [loanForm, setLoanForm] = useState({
    estimatedTaxFilingDate: "",
    estimatedExpenditureAmount: "",
  });
  const [loanInfo, setLoanInfo] = useState({
    loanTerm: 0,
    ezifinLoanInfo: null,
    competitorLoanInfo: null,
  } as LoanDetailsProps);

  // API Calls
  const loanCalculator = (data: any, formik: any): void => {
    LoanService.rndLoanCalculator(data)
      .then((response: any) => {
        const {data} = response.data;
        if (data) {
          setLoanInfo(data);
        }
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik.setSubmitting(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (formValues: any, formik: any): void => {
    formik.setSubmitting(true);
    const rawValue = formValues.estimatedExpenditureAmount.replace(/,/g, "");
    const requestPayload = {
      estimatedTaxFilingDate: formValues.estimatedTaxFilingDate,
      estimatedExpenditureAmount: rawValue,
    };
    loanCalculator(requestPayload, formik);
  };

  // UI Elements
  const getLoanInfo = (
    title: string,
    amount: string,
    isValidAmount: boolean,
  ): any => {
    return (
      <Box className="justify-space-between align-center">
        <Box className="flex-column">
          <Typography variant="body1" className="f14">
            {title}
          </Typography>
          <Typography variant="body1" className="f20 mt-3">
            $ {amount}
          </Typography>
        </Box>
        <img
          src={
            isValidAmount
              ? "/images/success-logo.png"
              : "/images/error-logo.png"
          }
          alt={isValidAmount ? "Success" : "Error"}
          width="20"
          height="20"
        />
      </Box>
    );
  };

  // Instance check
  const toNumber = (value: string | number | undefined | null): number => {
    if (!value) return 0;
    return Number(value.toString().replace(/,/g, ""));
  };

  const getEzifinLoanDetails = (): ReactElement => {
    const showEstimatedCheck =
      toNumber(loanInfo.ezifinLoanInfo?.estimatedRefund) >=
      toNumber(loanInfo.competitorLoanInfo?.estimatedRefund);

    const showMaxLoanAmountCheck =
      toNumber(loanInfo.ezifinLoanInfo?.maximumLoanAmount) >=
      toNumber(loanInfo.competitorLoanInfo?.maximumLoanAmount);

    const showExpectedRepaymentAmountCheck =
      toNumber(loanInfo.ezifinLoanInfo?.expectedRepaymentAmount) <=
      toNumber(loanInfo.competitorLoanInfo?.expectedRepaymentAmount);

    const showTotalFeesAndChargesCheck =
      toNumber(loanInfo.ezifinLoanInfo?.totalFeesAndCharges) <=
      toNumber(loanInfo.competitorLoanInfo?.totalFeesAndCharges);

    console.log("showTotalFeesAndChargesCheck", showTotalFeesAndChargesCheck);

    return (
      <Box className="flex-column">
        {getLoanInfo(
          "Estimated refund*",
          loanInfo.ezifinLoanInfo?.estimatedRefund,
          showEstimatedCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Maximum loan amount",
          loanInfo.ezifinLoanInfo?.maximumLoanAmount,
          showMaxLoanAmountCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Expected repayment amount",
          loanInfo.ezifinLoanInfo?.expectedRepaymentAmount,
          showExpectedRepaymentAmountCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Total fees and charges",
          loanInfo.ezifinLoanInfo?.totalFeesAndCharges,
          showTotalFeesAndChargesCheck,
        )}
      </Box>
    );
  };

  const getCompetitorLoanDetails = (): ReactElement => {
    const showEstimatedCheck =
      toNumber(loanInfo.competitorLoanInfo?.estimatedRefund) >
      toNumber(loanInfo.ezifinLoanInfo?.estimatedRefund);

    const showMaxLoanAmountCheck =
      toNumber(loanInfo.competitorLoanInfo?.maximumLoanAmount) >
      toNumber(loanInfo.ezifinLoanInfo?.maximumLoanAmount);

    const showExpectedRepaymentAmountCheck =
      toNumber(loanInfo.competitorLoanInfo?.expectedRepaymentAmount) <
      toNumber(loanInfo.ezifinLoanInfo?.expectedRepaymentAmount);

    const showTotalFeesAndChargesCheck =
      toNumber(loanInfo.competitorLoanInfo?.totalFeesAndCharges) <
      toNumber(loanInfo.ezifinLoanInfo?.totalFeesAndCharges);

    return (
      <Box className="flex-column">
        {getLoanInfo(
          "Estimated refund*",
          loanInfo.competitorLoanInfo?.estimatedRefund,
          showEstimatedCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Maximum loan amount",
          loanInfo.competitorLoanInfo?.maximumLoanAmount,
          showMaxLoanAmountCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Expected repayment amount",
          loanInfo.competitorLoanInfo?.expectedRepaymentAmount,
          showExpectedRepaymentAmountCheck,
        )}

        <Divider className="my-3" />
        {getLoanInfo(
          "Total fees and charges",
          loanInfo.competitorLoanInfo?.totalFeesAndCharges,
          showTotalFeesAndChargesCheck,
        )}
      </Box>
    );
  };

  return (
    <>
      <Dialog maxWidth="sm" fullWidth open={showForm}>
        <DialogTitle>
          <Box className="justify-end">
            <IconButton onClick={onCloseForm}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className="flex-column">
            <Typography variant="body1" className="f30 justify-center">
              How much money could you access?
            </Typography>

            <Typography variant="body1" className="text-center pt-5 px-1">
              Get your annual R&D Tax Incentive refund with an Ezifin R&D Loan.
              It's a smarter way to access the funds in advance. Estimate how
              much you could access now.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent className="loan-calculator-wrapper">
          <Typography style={{color: "#0053FD"}}>Start here:</Typography>
          <Formik
            enableReinitialize
            initialValues={loanForm}
            validationSchema={rndLoanCalculatorSchema}
            validateOnMount
            onSubmit={(formValues, formik) =>
              actionOnSubmit(formValues, formik)
            }
          >
            {({handleSubmit, setFieldValue, isSubmitting, isValid, values}) => (
              <form autoComplete="off">
                <Grid container spacing={2} className="pt-3">
                  <Grid size={{xs: 12, md: 5}}>
                    <Typography variant="body1">
                      Estimated tax filing date:
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker"]}>
                        <DatePicker
                          minDate={dayjs()}
                          format="DD/MM/YYYY"
                          maxDate={dayjs().add(391, "day")}
                          onChange={(date) => {
                            const formattedDate =
                              dayjs(date).format("YYYY-MM-DD");
                            setFieldValue(
                              "estimatedTaxFilingDate",
                              formattedDate,
                            );
                          }}
                          slotProps={{
                            day: {
                              sx: {
                                "&.MuiPickersDay-root.Mui-selected": {
                                  backgroundColor: "#2d68f7",
                                  color: "white",
                                },
                              },
                            },
                            textField: {
                              variant: "standard",
                              fullWidth: true,
                              margin: "normal",
                            },
                          }}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </Grid>
                  <Grid size={{xs: 12, md: 5}}>
                    <Typography variant="body1">R&D Expenditure:</Typography>
                    <Box className="mt-1">
                      <TextField
                        type="text"
                        name="estimatedExpenditureAmount"
                        fullWidth
                        margin="normal"
                        placeholder="1,000,000"
                        value={values.estimatedExpenditureAmount}
                        onChange={(event: any) => {
                          const rawValue = event.target.value.replace(
                            /\D/g,
                            "",
                          );

                          const formatted =
                            rawValue === ""
                              ? ""
                              : Number(rawValue).toLocaleString("en-US");

                          console.log("formatted", formatted);

                          setFieldValue(
                            "estimatedExpenditureAmount",
                            formatted,
                          );
                        }}
                        inputProps={{
                          inputMode: "numeric",
                        }}
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                $
                              </InputAdornment>
                            ),
                          },
                        }}
                        variant="standard"
                      />
                    </Box>
                  </Grid>
                  <Grid size={{xs: 12, md: 2}} className="align-center">
                    <AppPrimaryButton
                      title="Calculate"
                      isLoading={isSubmitting}
                      loadingText="Calculating..."
                      onClick={handleSubmit}
                      disabled={!isValid || isSubmitting}
                    />
                  </Grid>
                </Grid>
                {isSubmitting && (
                  <Box className="empty-content">
                    <CircularProgress />
                  </Box>
                )}
                {!isSubmitting && loanInfo.loanTerm > 0 && (
                  <>
                    <Box className="mt-3 days-container">
                      <Typography variant="body1">
                        Duration of loan: {loanInfo.loanTerm}{" "}
                        {loanInfo.loanTerm > 1 ? "days" : "day"}
                      </Typography>
                    </Box>

                    <Box className="mt-3 tab-content-container">
                      <Grid container spacing={4} className="pt-3">
                        <Grid size={{xs: 12, md: 6}}>
                          <ToggleButton
                            value="center"
                            aria-label="centered"
                            fullWidth
                            className="toggle-button toggle-button-active"
                            disabled
                          >
                            <img
                              src="/images/ezifin-logo-small.png"
                              alt="Ezifin"
                              width="100"
                            />
                          </ToggleButton>

                          <Box className="content content-active mt-4">
                            {getEzifinLoanDetails()}
                          </Box>
                        </Grid>
                        <Grid size={{xs: 12, md: 6}}>
                          <ToggleButton
                            value="center"
                            aria-label="centered"
                            fullWidth
                            className="toggle-button"
                            disabled
                          >
                            <Typography variant="body1" className="">
                              Others
                            </Typography>
                          </ToggleButton>

                          <Box className="content mt-4">
                            {getCompetitorLoanDetails()}
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box className="mt-4">
                      <Typography variant="body1" className="f12">
                        * to be verified by approved R&D tax consultant
                      </Typography>
                      <Typography variant="body1" className="f12">
                        calculated as 60 days after tax filing date
                      </Typography>
                    </Box>
                  </>
                )}
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LoanCalculator;
